import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { Search, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { toast } from "react-toastify";

interface Business {
  id: string;
  name?: string | null;
  website?: string | null;
  created_at?: string | null;
  _count: { User: number; Store: number };
  BusinessPackageSubscription?: {
    is_active: boolean;
    billing_cycle?: string | null;
    end_date?: string | null;
    Package?: { name?: string | null } | null;
  } | null;
}

const SuperAdminBusinesses = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchBusinesses = useCallback(() => {
    setLoading(true);
    superAdminApi
      .get("/superadmin/businesses", { params: { page, limit: 20, search } })
      .then((res) => {
        setBusinesses(res.data.businesses);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      })
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load businesses"))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Businesses</h1>
            <p className="text-gray-500 text-sm mt-1">{total.toLocaleString()} total businesses</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-5">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search businesses..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors">
            Search
          </button>
        </form>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Business</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Users</th>
                  <th className="px-4 py-3 font-medium">Stores</th>
                  <th className="px-4 py-3 font-medium">Sub Status</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gray-200 flex-shrink-0" />
                          <div>
                            <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
                            <div className="h-3 bg-gray-200 rounded w-24" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    </tr>
                  ))
                ) : businesses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">No businesses found</td>
                  </tr>
                ) : (
                  businesses.map((biz) => (
                    <tr key={biz.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/superadmin/businesses/${biz.id}`)}
                          className="flex items-center gap-2 text-left hover:text-indigo-600 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{biz.name ?? "Unnamed"}</p>
                            {biz.website && <p className="text-xs text-gray-400">{biz.website}</p>}
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{biz.BusinessPackageSubscription?.Package?.name ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{biz._count.User}</td>
                      <td className="px-4 py-3 text-gray-600">{biz._count.Store}</td>
                      <td className="px-4 py-3">
                        {biz.BusinessPackageSubscription ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            biz.BusinessPackageSubscription.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}>
                            {biz.BusinessPackageSubscription.is_active ? "Active" : "Expired"}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {biz.created_at ? new Date(biz.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminBusinesses;
